import React, {useState, useRef, useCallback, useMemo, useEffect} from 'react';
import s from '../../s';
import * as util from '../../../util';
import ModalSimple from '../../../global/modal-simple';
import ReactCrop, {centerCrop, Crop, makeAspectCrop, PixelCrop} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {runInAction} from 'mobx';
import {observer} from 'mobx-react';
import InfoIcon from '../shared/info-icon';
import {imageUploadStore} from './image-upload.store';
import axios from 'axios';
import {getAntiForgeryToken} from '../../../util';

const centerAspectCrop = (width: number, height: number, aspect: number) => {
    return centerCrop(
        makeAspectCrop(
            {
                unit: '%',
                width: 100,
            },
            aspect,
            width,
            height,
        ),
        width,
        height,
    );
};

const ImageUpload: React.FC = observer(() => {
    const [clicked, setClicked] = useState<boolean>(false);
    const [imgSrc, setImgSrc] = useState<string>('');
    const [aspect, setAspect] = useState<number>(0);
    const [crop, setCrop] = useState<Crop | undefined>(undefined);
    const [completedCrop, setCompletedCrop] = useState<PixelCrop | undefined>(undefined);
    const imgRef = useRef<HTMLImageElement>(null);
    const canUpload = useMemo(() => clicked, [clicked]);

    useEffect(() => {
        s.getPPIValuesForImageUpload();
    }, []);

    const checkResoLevel = useCallback(() => {
        if (s.ppi_w_actual < s.ppi_w || s.ppi_h_actual < s.ppi_h) {
            imageUploadStore.reso_level = 2;
        } else if (s.ppi_w_actual < s.ppi_w_max || s.ppi_h_actual < s.ppi_h_max) {
            imageUploadStore.reso_level = 1;
        }
    }, []);

    const showImage = useCallback(() => {
        const url = URL.createObjectURL(imageUploadStore.file);

        if (url) {
            runInAction(() => {
                s.ps.override_d1_img = url;
                s.ui.image_uploaded = true;
                s.ui.doRender = true;
            });
            s.pulse();
        } else {
            imageUploadStore.imageUploadError('');
        }
    }, []);

    const save = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (!completedCrop) return;

            runInAction(() => {
                s.ppi_w_actual = Math.round(completedCrop.width);
                s.ppi_h_actual = Math.round(completedCrop.height);
            });

            checkResoLevel();

            if (imageUploadStore.reso_level !== 2) {
                showImage();
            } else {
                imageUploadStore.cancel();
            }
        },
        [completedCrop, checkResoLevel, showImage],
    );

    const createCroppedImage = useCallback((cropData: PixelCrop) => {
        if (imgRef.current && cropData.width && cropData.height) {
            const {naturalWidth, naturalHeight, width, height} = imgRef.current;
            const scaleX = naturalWidth / width;
            const scaleY = naturalHeight / height;

            const canvas = document.createElement('canvas');
            canvas.width = cropData.width;
            canvas.height = cropData.height;

            const ctx = canvas.getContext('2d');

            if (!ctx) return;

            ctx.drawImage(imgRef.current, cropData.x * scaleX, cropData.y * scaleY, cropData.width * scaleX, cropData.height * scaleY, 0, 0, cropData.width, cropData.height);

            canvas.toBlob(blob => {
                if (!blob) return;
                imageUploadStore.file = new File([blob], imageUploadStore.file.name, {type: imageUploadStore.file.type});
            });

            setCompletedCrop({
                x: cropData.x * scaleX,
                y: cropData.y * scaleY,
                width: cropData.width * scaleX,
                height: cropData.height * scaleY,
                unit: 'px',
            });
        }
    }, []);

    const clear = useCallback(async (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        runInAction(() => {
            s.ui.image_uploaded = false;
            s.ui.image_uploaded_msg = '';
            s.ps.image_upload_hash3 = '';
            s.ps.override_d1_img = '';
            s.ui.image_uploading = false;
            s.ui.doRender = true;
            s.ui.update_pricing = true;
        });
        imageUploadStore.reso_level = 0;
        URL.revokeObjectURL(s.ps.override_d1_img);
        s.pulse();
    }, []);

    const selectFile = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length) {
            let file = e.target.files[0];
            const isPDF = file.type === 'application/pdf';

            if (isPDF) {
                try {
                    s.ui.image_uploading = true;

                    const url = '/api/PDFPNG/convert';
                    const config = {
                        headers: {
                            RequestVerificationToken: getAntiForgeryToken(),
                        },
                        responseType: 'blob' as const,
                    };
                    const response = await axios.post(url, imageUploadStore.getFormData(file), config);
                    const blob = response.data;

                    if (blob) {
                        const fileName = file.name.replace('.pdf', '.png');

                        file = new File([blob], fileName, {type: 'image/png'});
                        s.ui.image_uploading = false;
                    }
                } catch (err) {
                    console.error(err);
                    imageUploadStore.imageUploadError();
                    s.ui.image_uploading = false;

                    return;
                }
            }

            imageUploadStore.file = file;
            imageUploadStore.reso_level = 0;
            setImgSrc('');
            setClicked(true);

            const reader = new FileReader();

            reader.addEventListener('load', () => setImgSrc(reader.result?.toString() || ''));
            reader.readAsDataURL(imageUploadStore.file);
            util.modal('modal-image-crop');
            setCrop(undefined);
        }
    }, []);

    const handleCenterCrop = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
        const newAspect = s.ps.docWidth / s.ps.docHeight;
        setAspect(newAspect);
        if (newAspect) {
            const {width, height} = e.currentTarget;
            setCrop(centerAspectCrop(width, height, newAspect));
        }
    }, []);

    if (!s.ps.is_caf || !s.ui.show_cust || !s.ui.show_grad_year) {
        return (
            <div className="gray-box">
                <div style={{padding: '6px 0px 12px 0px'}}>
                    <h1>{s.prod.DocType} Upload</h1>
                    <div style={{paddingTop: '6px'}}>
                        {!s.ui.image_uploaded && (
                            <input
                                id="imageUploadInput"
                                type="file"
                                accept="image/*, .pdf"
                                onChange={selectFile}
                            />
                        )}
                        {s.ui.image_uploaded && (
                            <button
                                type="submit"
                                className="prod-atc-btn"
                                style={{fontSize: '1rem', padding: '6px 12px'}}
                                onClick={clear}
                            >
                                Remove
                            </button>
                        )}
                    </div>
                    <div>
                        <ModalSimple
                            maxWidth={'800px'}
                            id="modal-image-crop"
                            title={'Image Crop'}
                            close={imageUploadStore.cancel}
                        >
                            <div>
                                <p>Please crop the image first before proceeding.</p>
                                <div style={{textAlign: 'center'}}>
                                    {!!imgSrc && (
                                        <ReactCrop
                                            onChange={(_, percentCrop) => setCrop(percentCrop)}
                                            onComplete={cropData => createCroppedImage(cropData)}
                                            crop={crop}
                                            aspect={aspect}
                                            minHeight={100}
                                        >
                                            <img
                                                onLoad={handleCenterCrop}
                                                ref={imgRef}
                                                src={imgSrc}
                                                alt="Crop Image"
                                            />
                                        </ReactCrop>
                                    )}
                                    <form
                                        id="form-fiu"
                                        onSubmit={save}
                                    >
                                        {!!completedCrop && canUpload && (
                                            <button
                                                type="submit"
                                                className="prod-atc-btn"
                                                style={{
                                                    fontSize: '1rem',
                                                    padding: '6px 12px',
                                                    marginTop: '10px',
                                                }}
                                                disabled={s.ui.image_uploading}
                                                data-dismiss="modal"
                                            >
                                                Upload
                                            </button>
                                        )}
                                    </form>
                                </div>
                            </div>
                        </ModalSimple>
                    </div>

                    {!s.ui.image_uploaded && s.ui.image_uploaded_msg !== '' && (
                        <div style={{paddingTop: '6px'}}>
                            <div className="alert alert-danger">
                                <p>
                                    <b>Upload {s.prod.DocType} Image</b>
                                </p>
                                <p>{s.ui.image_uploaded_msg}</p>
                            </div>
                        </div>
                    )}

                    {imageUploadStore.reso_level === 2 && (
                        <div style={{paddingTop: '6px'}}>
                            <div className="alert alert-danger">
                                <p>
                                    <b>Image Resolution Error</b>
                                </p>
                                <p>
                                    A minimum resolution of {s.ppi_w}x{s.ppi_h} pixels is required for this frame. This baseline ensures that your photos maintain a basic level of clarity and detail when printed. For an enhanced visual experience, we strongly recommend uploading images with a
                                    resolution of at least {s.ppi_w_max}x{s.ppi_h_max} pixels. Higher resolution images allow for finer details to be captured and displayed, resulting in a more vibrant and lifelike print. The resolution of the image uploaded is {s.ppi_w_actual}x{s.ppi_h_actual}.
                                </p>
                            </div>
                        </div>
                    )}

                    {imageUploadStore.reso_level === 1 && (
                        <div style={{paddingTop: '6px'}}>
                            <div className="alert alert-info">
                                <p>
                                    <span style={{fontSize: '1rem', fontWeight: 700}}>Image Size, Cropping and Aspect Ratio</span>
                                </p>

                                {imageUploadStore.reso_level > 0 && (
                                    <p style={{paddingTop: 6, paddingLeft: 12}}>
                                        <div
                                            className="modal-sub-header"
                                            style={{fontSize: '0.95rem'}}
                                        >
                                            Image size
                                        </div>
                                        Your image is{' '}
                                        <span style={{fontWeight: 700}}>
                                            {s.ppi_w}x{s.ppi_h} pixels
                                        </span>
                                        .
                                    </p>
                                )}

                                {imageUploadStore.reso_level === 1 && (
                                    <p style={{paddingTop: 6, paddingLeft: 12}}>
                                        <div
                                            className="modal-sub-header"
                                            style={{fontSize: '0.95rem'}}
                                        >
                                            Current print clarity
                                        </div>
                                        Your photo has the basic level of clarity and detail when printed.
                                    </p>
                                )}

                                {imageUploadStore.reso_level === 1 && (
                                    <p style={{paddingTop: 6, paddingLeft: 12}}>
                                        <div
                                            className="modal-sub-header"
                                            style={{fontSize: '0.95rem'}}
                                        >
                                            Optimal print quality
                                        </div>
                                        For an enhanced visual experience, we strongly recommend uploading images with a resolution of at least{' '}
                                        <span style={{fontWeight: 700}}>
                                            {s.ppi_w_max}x{s.ppi_h_max} pixels
                                        </span>
                                        . Higher resolution images allow for finer details to be captured and displayed, resulting in a more vibrant and lifelike print.
                                    </p>
                                )}

                                {imageUploadStore.reso_level === 1 && (
                                    <p style={{paddingTop: 6, paddingLeft: 12}}>
                                        <div
                                            className="modal-sub-header"
                                            style={{fontSize: '0.95rem'}}
                                        >
                                            Printing at your selected size
                                        </div>
                                        This image can still be printed at your selected size, but it may appear slightly soft or less detailed when viewed up close. If you have a higher resolution version of this photo, uploading it will give the best result.
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {imageUploadStore.reso_level === 0 && (
                        <div style={{paddingTop: 18}}>
                            <div>
                                <p>
                                    <span style={{fontSize: '1rem', fontWeight: 700}}>Image Size, Cropping and Aspect Ratio</span>
                                </p>

                                <p style={{paddingTop: 12, paddingLeft: 12}}>
                                    <div
                                        className="modal-sub-header"
                                        style={{fontSize: '0.95rem'}}
                                    >
                                        Minimum required
                                    </div>
                                    A minimum resolution of {s.ppi_w}x{s.ppi_h} pixels is required for this frame. This baseline ensures that your photos maintain a basic level of clarity and detail when printed.
                                </p>

                                <p style={{paddingTop: 6, paddingLeft: 12}}>
                                    <div
                                        className="modal-sub-header"
                                        style={{fontSize: '0.95rem'}}
                                    >
                                        Recommended for best results
                                    </div>
                                    For an enhanced visual experience, we strongly recommend uploading images with a resolution of at least{' '}
                                    <span style={{fontWeight: 700}}>
                                        {s.ppi_w_max}x{s.ppi_h_max} pixels
                                    </span>
                                    . Higher resolution images allow for finer details to be captured and displayed, resulting in a more vibrant and lifelike print.
                                </p>

                                <p style={{paddingTop: 6, paddingLeft: 12}}>
                                    <div
                                        className="modal-sub-header"
                                        style={{fontSize: '0.95rem'}}
                                    >
                                        Accepted file types
                                    </div>
                                    Formats accepted: PNG (recommended), JPG or PDF
                                </p>
                            </div>
                        </div>
                    )}
                </div>
                <div>
                    <ModalSimple
                        id="modal-image-crop-info"
                        title={'Image Size, Cropping and Aspect Ratio'}
                    >
                        <div>
                            {imageUploadStore.reso_level !== 1 && (
                                <p>
                                    <div className="modal-sub-header">Minimum required</div>A minimum resolution of{' '}
                                    <span style={{fontWeight: 700}}>
                                        {s.ppi_w}x{s.ppi_h} pixels
                                    </span>{' '}
                                    is required for this frame. This baseline ensures that your photos maintain a basic level of clarity and detail when printed.
                                </p>
                            )}
                            {imageUploadStore.reso_level > 0 && (
                                <p>
                                    <div className="modal-sub-header">Image size</div>
                                    Your image is{' '}
                                    <span style={{fontWeight: 700}}>
                                        {s.ppi_w}x{s.ppi_h} pixels
                                    </span>
                                    .
                                </p>
                            )}
                            {imageUploadStore.reso_level === 1 && (
                                <p>
                                    <div className="modal-sub-header">Current print clarity</div>
                                    Your photo has the basic level of clarity and detail when printed.
                                </p>
                            )}
                            <p>
                                <div className="modal-sub-header">Optimal print quality</div>
                                For an enhanced visual experience, we strongly recommend uploading images with a resolution of at least{' '}
                                <span style={{fontWeight: 700}}>
                                    {s.ppi_w_max}x{s.ppi_h_max} pixels
                                </span>
                                . Higher resolution images allow for finer details to be captured and displayed, resulting in a more vibrant and lifelike print.
                            </p>
                            {imageUploadStore.reso_level === 1 && (
                                <p>
                                    <div className="modal-sub-header">Printing at your selected size</div>
                                    This image can still be printed at your selected size, but it may appear slightly soft or less detailed when viewed up close. If you have a higher resolution version of this photo, uploading it will give the best result.
                                </p>
                            )}
                            <p>
                                <div className="modal-sub-header">Accepted file types</div>
                                Formats accepted: PNG (recommended), JPG or PDF
                            </p>
                            {s.si.UploadFee > 0 && <p>A printing fee of {util.currency(s.si.UploadFee)} will be added to the item upon upload.</p>}
                        </div>
                    </ModalSimple>
                    <span style={{fontSize: '0.875rem'}}>
                        Image size, cropping and aspect ratio
                        <InfoIcon
                            id="modal-image-crop-info"
                            ariaLabel="View information about Image Size, Cropping and Aspect Ratio"
                            useMargin={true}
                        />
                    </span>
                </div>
            </div>
        );
    } else {
        return <div className="hide" />;
    }
});

export default ImageUpload;
